import React from "react";
import {
  StyledForm,
  ButtonGroup,
  BackButton,
  SubmitButton,
  FormLabel,
} from "./styles";
import { navigate } from "@reach/router";
import { Formik } from "formik";
import { CustomFileUpload } from "../../../common/FormInputs";
import { PageWrapper, Card, FormGrid, Section } from "./SplitAndUpload"; // Adjust path if needed
import useLoader from "../../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../../helpers/functions";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import workerSrc from "pdfjs-dist/legacy/build/pdf.worker.entry";
import { saveAs } from "file-saver";
import { podValidationSchema } from "../../../../helpers/validationSchema";

pdfjsLib.GlobalWorkerOptions.isEvalSupported = false;

pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

const sanitizeFilename = (filename) => {
  let name = filename.replace(/[<>:"/\\|?*]/g, "_");
  name = name.replace(/\s+/g, " ").trim();
  return name;
};

const SplitAndRenamePOD = ({ menuItems }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const handleProcess = async (values) => {
    const { zipFile, excelFile } = values;
    if (!zipFile || !excelFile) return;

    setLoader({ state: true, message: "Processing POD files..." });

    try {
      const awbToInfo = await readExcel(excelFile);

      const zip = await JSZip.loadAsync(zipFile);
      let pdfBytes = null;
      for (const filename in zip.files) {
        if (filename.toLowerCase().endsWith(".pdf")) {
          pdfBytes = await zip.files[filename].async("arraybuffer");
          break;
        }
      }
      if (!pdfBytes) throw new Error("No PDF file found in the ZIP");

      const pdfDoc = await PDFDocument.load(pdfBytes);
      const numPages = pdfDoc.getPageCount();
      const pdfjsDoc = await pdfjsLib.getDocument({ data: pdfBytes }).promise;

      const newZip = new JSZip();

      for (let i = 0; i < numPages; i++) {
        const page = await pdfjsDoc.getPage(i + 1);
        const textContent = await page.getTextContent();
        const text = textContent.items.map((item) => item.str).join(" ");

        const matches = text.match(/[A-Z]{2}\d{9}IN/g);
        const awb = matches ? matches[0] : null;

        let newFilename = `page_${i + 1}.pdf`;

        if (awb && awbToInfo.has(awb)) {
          const { contract, address } = awbToInfo.get(awb);
          let baseName = `_${awb}_${contract}_${address}.pdf`;
          baseName = sanitizeFilename(baseName);

          newFilename = baseName;
          let counter = 1;
          let finalName = newFilename;
          while (newZip.file(finalName)) {
            const nameWithoutExt = finalName.replace(".pdf", "");
            finalName = `${nameWithoutExt}_${counter}.pdf`;
            counter++;
          }
          newFilename = finalName;
        }

        const newPdf = await PDFDocument.create();
        const [copiedPage] = await newPdf.copyPages(pdfDoc, [i]);
        newPdf.addPage(copiedPage);
        const singlePageBytes = await newPdf.save();
        newZip.file(newFilename, singlePageBytes);
      }

      const outputZipBlob = await newZip.generateAsync({ type: "blob" });
      saveAs(outputZipBlob, "Renamed_POD_Lot1.zip");

      enqueueSnackbar("Processing completed successfully!", {
        variant: "success",
      });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(getErrorMessage(error) || " Something went wrong", {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <PageWrapper>
      <Card>
        <Formik
          initialValues={{
            zipFile: "",
            excelFile: "",
          }}
          validationSchema={podValidationSchema}
          onSubmit={handleProcess}
        >
          {({ values, errors, touched, setFieldValue, handleSubmit }) => (
            <StyledForm>
              <Section>
                <FormGrid>
                  <div>
                    <FormLabel>
                      POD ZIP File <span className="required-star">*</span>
                    </FormLabel>
                    <CustomFileUpload
                      accept=".zip"
                      setFile={(file) => setFieldValue("zipFile", file)}
                      file={{ name: values.zipFile?.name || "" }}
                      error={touched["zipFile"] && errors["zipFile"]}
                    />
                  </div>

                  <div>
                    <FormLabel>
                      Rename Excel File <span className="required-star">*</span>
                    </FormLabel>
                    <CustomFileUpload
                      accept=".xlsx"
                      setFile={(file) => setFieldValue("excelFile", file)}
                      file={{ name: values.excelFile?.name || "" }}
                      error={touched["excelFile"] && errors["excelFile"]}
                    />
                  </div>
                </FormGrid>
              </Section>

              <ButtonGroup>
                <BackButton onClick={() => navigate("/dashboard/case-manager")}>
                  Back
                </BackButton>
                <SubmitButton type="button" onClick={handleSubmit}>
                  Submit
                </SubmitButton>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      </Card>
    </PageWrapper>
  );
};

// Helper
const readExcel = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(sheet);

      const map = new Map();
      jsonData.forEach((row) => {
        if (row["AWB no"]) {
          map.set(row["AWB no"], {
            contract: row["CONTRACT NUMBER"] || "",
            address: row["Address"] || "",
          });
        }
      });
      resolve(map);
    };
    reader.readAsArrayBuffer(file);
  });
};

export default SplitAndRenamePOD;
